/**
 * Knowledge Graph node and relationship styling constants.
 * Maps Neo4j labels to colors and sizes for NVL visualization.
 */

export const KG_NODE_STYLES: Record<string, { color: string; size: number }> = {
  // Core entities
  CspCustomer:        { color: '#3b82f6', size: 30 },  // blue-500
  Opportunity:        { color: '#10b981', size: 22 },   // emerald-500
  AeAccount:          { color: '#f59e0b', size: 28 },   // amber-500
  Territory:          { color: '#8b5cf6', size: 26 },   // violet-500

  // Tier 1 — relationship entities
  Stakeholder:        { color: '#ec4899', size: 20 },   // pink-500
  Meeting:            { color: '#06b6d4', size: 18 },   // cyan-500
  MeetingNote:        { color: '#67e8f9', size: 14 },   // cyan-300
  Competitor:         { color: '#ef4444', size: 22 },   // red-500
  TeamMember:         { color: '#a78bfa', size: 16 },   // violet-400
  PersonalIntel:      { color: '#f472b6', size: 16 },   // pink-400

  // Tier 2 — artifacts (teal/green family)
  CommandPlan:        { color: '#14b8a6', size: 18 },   // teal-500
  Meddpicc:           { color: '#2dd4bf', size: 18 },   // teal-400
  DapStrategy:        { color: '#0d9488', size: 18 },   // teal-600
  TechnicalClosePlan: { color: '#0891b2', size: 18 },   // cyan-600
  ValueStream:        { color: '#059669', size: 18 },   // emerald-600
  CspPlan:            { color: '#34d399', size: 18 },   // emerald-400
  BattleCard:         { color: '#f97316', size: 18 },   // orange-500
  CrossSell:          { color: '#84cc16', size: 16 },   // lime-500
  CoachingInsight:    { color: '#eab308', size: 16 },   // yellow-500

  // Tier 3 — content
  Document:           { color: '#94a3b8', size: 12 },   // slate-400
  MissionDetail:      { color: '#64748b', size: 14 },   // slate-500

  // Element nodes — per-customer
  PainPoint:          { color: '#f87171', size: 14 },   // red-400
  Objective:          { color: '#60a5fa', size: 14 },   // blue-400
  FeatureMatch:       { color: '#818cf8', size: 14 },   // indigo-400
  Risk:               { color: '#fb7185', size: 14 },   // rose-400
  Bottleneck:         { color: '#fbbf24', size: 14 },   // amber-400
  DapUseCase:         { color: '#22d3ee', size: 14 },   // cyan-400

  // Element nodes — shared (hub nodes, slightly larger)
  Tool:               { color: '#fb923c', size: 16 },   // orange-400
  ValueDriver:        { color: '#facc15', size: 18 },   // yellow-400
  GitLabProduct:      { color: '#8b5cf6', size: 20 },   // violet-500

  // ITDB
  Agency:             { color: '#7c3aed', size: 24 },   // violet-600
  Investment:         { color: '#a855f7', size: 14 },   // purple-500
}

export const KG_RELATIONSHIP_COLORS: Record<string, string> = {
  HAS_OPPORTUNITY: '#10b981',
  MANAGED_BY: '#f59e0b',
  IN_TERRITORY: '#8b5cf6',
  ASSIGNED_TO: '#f59e0b',
  PARENT_OF: '#8b5cf6',
  HAS_STAKEHOLDER: '#ec4899',
  INFLUENCES: '#f97316',
  BLOCKS: '#ef4444',
  SPONSORS: '#22c55e',
  REPORTS_TO: '#6b7280',
  BELONGS_TO: '#7c3aed',
  MANAGES: '#a855f7',
  HAS_MEETING: '#06b6d4',
  HAS_NOTE: '#67e8f9',
  HAS_COMPETITOR: '#ef4444',
  HAS_TEAM_MEMBER: '#a78bfa',
  HAS_DOCUMENT: '#94a3b8',
  HAS_PLAN: '#34d399',
  HAS_COMMAND_PLAN: '#14b8a6',
  HAS_MEDDPICC: '#2dd4bf',
  HAS_DAP_STRATEGY: '#0d9488',
  HAS_TCP: '#0891b2',
  HAS_VALUE_STREAM: '#059669',
  HAS_BATTLE_CARD: '#f97316',
  HAS_CROSS_SELL: '#84cc16',
  HAS_COACHING: '#eab308',
  HAS_PERSONAL_INTEL: '#f472b6',
  // Element relationships
  HAS_PAIN_POINT: '#f87171',
  HAS_OBJECTIVE: '#60a5fa',
  MATCHED_TO_FEATURE: '#818cf8',
  FOR_PRODUCT: '#8b5cf6',
  USES_TOOL: '#fb923c',
  DRIVEN_BY: '#facc15',
  HAS_RISK: '#fb7185',
  HAS_BOTTLENECK: '#fbbf24',
  HAS_DAP_USE_CASE: '#22d3ee',
}

export const DEFAULT_RELATIONSHIP_COLOR = '#475569' // slate-600
export const DEFAULT_NODE_STYLE = { color: '#6b7280', size: 16 } // gray-500
